// src/v2/leases/LeaseDatesPane.jsx
//
// LEASE-DATES-REDESIGN: Reimagined Dates tab.
//
// Three-row layout (top → bottom):
//   Row 1:  NextKeyDateHero + DateFlagsPane
//   Row 2:  DatesLeaseTimeline — SVG proportional timeline
//   Row 3:  HistoryList — past dates, descending; clause text behind "details ▾"
//   Inline: add-date form (preserved from V5-S4)
//
// No new server calls — receives criticalDates, lease, abatementAllowances from
// LeaseDetailBody, which already fetches all of this data.

import React, { useMemo, useState, useEffect } from "react";
import { T } from "../../app/tokens.js";
import { useChartHoverIntent } from "../../components/useChartHoverIntent.js";
import {
  BUCKET_BG,
  BUCKET_FG,
  BUCKET_LABEL,
} from "../../features/leases/components/CriticalDateRow.jsx";
import {
  groupCriticalDates,
  buildTimelineEvents,
  fmtYearsOut,
  selectCurrentTermKeyDates,
  selectCurrentTermNoticeWindow,
} from "../../features/leases/leaseDetailDatesHelpers.js";
import { parseDateOnly, isSentinelEndDate } from "../../features/leases/leaseTabConfig.js";
import { addCriticalDate } from "../../data/leasesClient.js";
import { getPulseCategoryImages } from "../../data/pulseClient.js";

const CD_TYPE_OPTIONS = [
  { value: "renewal_notice",    label: "Renewal notice"    },
  { value: "break_option",      label: "Break option"      },
  { value: "expiration",        label: "Expiration"        },
  { value: "escalation",        label: "Escalation"        },
  { value: "deposit_burndown",  label: "Deposit burndown"  },
  { value: "allowance_deadline",label: "Allowance deadline"},
  { value: "allowance_warning", label: "Allowance warning" },
  { value: "allowance_expiry",  label: "Allowance expiry"  },
  { value: "cam_audit",         label: "CAM audit"         },
  { value: "other",             label: "Other"             },
];

// Action bucket types — used by History's existing "needs attention" marker.
const ACTION_BUCKETS = new Set([
  "renewal_notice",
  "break_option",
  "break_notice",
  "expiration",
  "allowance_deadline",
  "allowance_warning",
  "allowance_expiry",
  "deposit_burndown",
]);

// Canonical tag-type map: every known cd type → one of the 5 display variants.
// "decision" → purple   "expiry" → red   "escalation" → gray-blue
// "abatement" → amber   "commence" → green
const CD_TYPE_TO_TAG = {
  renewal_notice:     "decision",
  break_option:       "decision",
  break_notice:       "decision",
  expiration:         "expiry",
  key_date:           "expiry",
  escalation:         "escalation",
  cam_audit:          "escalation",
  deposit_burndown:   "abatement",
  allowance_deadline: "abatement",
  allowance_warning:  "abatement",
  allowance_expiry:   "abatement",
  commencement:       "commence",
  other:              "other",
};

// Derive the tag variant for any cd type (upcoming or history).
function cdTagType(type) {
  return CD_TYPE_TO_TAG[type] || "other";
}

// ─── Date formatting ──────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null;
  const dt = parseDateOnly(d);
  if (!dt) return String(d);
  return dt.toLocaleDateString("en-US", {
    year:  "numeric",
    month: "long",
    day:   "numeric",
  });
}

// ─── Type tag chip ─────────────────────────────────────────────────────────────

// Five typed chip variants — colors via T tokens (all hex lives in tokens.js).
const TAG_OVERRIDES = {
  decision:   { bg: T.chipDecisionBg,  fg: T.chipDecisionFg,     text: "Decision"   },
  expiry:     { bg: T.chipExpiryBg,    fg: T.ragRed,             text: "Expiry"     },
  escalation: { bg: T.tabBadgeInactiveBg, fg: T.tabBadgeInactiveFg, text: "Escalation" },
  abatement:  { bg: T.chipAbatementBg, fg: T.ragAmberText,       text: "Abatement"  },
  commence:   { bg: T.chipCommenceBg,  fg: T.ragGreenText,       text: "Commence"   },
  other:      { bg: T.lightGray,       fg: T.mid,                text: "Other"      },
};

function TypeTag({ type }) {
  const override = TAG_OVERRIDES[type];
  const bg   = override ? override.bg   : (BUCKET_BG[type]    || BUCKET_BG.other);
  const fg   = override ? override.fg   : (BUCKET_FG[type]    || BUCKET_FG.other);
  const text = override ? override.text : (BUCKET_LABEL[type] || "Other");
  return (
    <span
      data-testid="register-bucket-chip"
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        justifyContent: "center",
        flexShrink:   0,
        width:        88,
        textAlign:    "center",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwSemibold,
        color:        fg,
        background:   bg,
        borderRadius: 5,
        padding:      "1px 7px",
        whiteSpace:   "nowrap",
      }}
    >
      {text}
    </span>
  );
}

// ─── Section group header ─────────────────────────────────────────────────────

function GroupHeader({ label, count }) {
  return (
    <div style={{
      display:    "flex",
      alignItems: "center",
      gap:        9,
      margin:     "0 2px 8px",
    }}>
      {/* Blue left-accent bar */}
      <div style={{
        width:        3,
        height:       15,
        background:   T.blue,
        borderRadius: 2,
        flexShrink:   0,
      }} />
      <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
        {label}
      </span>
      {count != null && (
        <span style={{
          fontSize:     T.fsXxs,
          fontWeight:   T.fwSemibold,
          background:   T.bg,
          color:        T.mid,
          borderRadius: 999,
          padding:      "2px 8px",
        }}>
          {count}
        </span>
      )}
    </div>
  );
}

// ─── NextKeyDateHero + DateFlagsPane ──────────────────────────────────────────

const KEY_DATE_LABEL = {
  expiration: "Lease expiry",
  renewal_notice: "Renewal notice",
  notice_deadline: "Notice deadline",
  break_option: "Break option",
  break_notice: "Break notice",
  renewal_option: "Renewal option",
};

function dateValue(value) {
  if (!value) return null;
  const parsed = parseDateOnly(value);
  return parsed ? { raw: value, date: parsed } : null;
}

function realLeaseEndDate(lease) {
  if (!lease?.endDate || isSentinelEndDate(lease.endDate)) return null;
  const end = dateValue(lease.endDate);
  return end && end.date.getFullYear() <= 2100 ? end : null;
}

function localDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function valueDateKey(value) {
  if (value instanceof Date) return localDateKey(value);
  return String(value ?? "").slice(0, 10);
}

function nearestCurrentTermDate(dates, types, today) {
  const todayKey = localDateKey(today);
  const matching = dates.filter((cd) => types.has(cd.type));
  return matching.find((cd) => cd._dateKey >= todayKey)
    || matching[matching.length - 1]
    || null;
}

function renewalTermLabel(lease) {
  const count = Number(lease?.renewalCount ?? lease?.renewal_count) || 0;
  const years = Number(lease?.renewalTermYears ?? lease?.renewal_term_years) || 0;
  if (count > 0 && years > 0) return `${count} × ${years}-yr`;
  if (count > 0) return `${count} option${count === 1 ? "" : "s"}`;
  return null;
}

function termMetadata(lease, currentTermStart) {
  const type = lease?.leaseType
    ? String(lease.leaseType).replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;
  const start = dateValue(currentTermStart || lease?.startDate);
  const end = realLeaseEndDate(lease);
  const term = start || end
    ? `${start ? start.date.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"} – ${end ? end.date.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}`
    : null;
  return [type, term].filter(Boolean).join(" · ") || "Lease term unavailable";
}

function elapsedTermPercent(lease, today, currentTermStart) {
  const start = dateValue(currentTermStart || lease?.startDate)?.date;
  const end = realLeaseEndDate(lease)?.date;
  if (!start || !end || end <= start) return null;
  const elapsed = Math.min(Math.max(today.getTime() - start.getTime(), 0), end.getTime() - start.getTime());
  return Math.round((elapsed / (end.getTime() - start.getTime())) * 100);
}

function getNextKeyDate(criticalDates, lease, today) {
  const selection = selectCurrentTermKeyDates(criticalDates, lease, today);
  const candidates = [];
  const expiryDate = realLeaseEndDate(lease);
  if (expiryDate) candidates.push({
    type: "expiration",
    dueDate: expiryDate.raw,
    _date: expiryDate.date,
    _dateKey: valueDateKey(expiryDate.raw),
  });

  candidates.push(...selection.eligibleDates);
  const todayKey = localDateKey(today);

  return candidates
    .filter((item) => item._dateKey >= todayKey)
    .sort((a, b) => a._dateKey.localeCompare(b._dateKey))[0] || null;
}

function NextKeyDateHero({ item, lease, today, currentTermStart, imageUrl }) {
  const daysRemaining = item
    ? Math.ceil((item._date.getTime() - today.getTime()) / 86400000)
    : null;
  const countdown = daysRemaining == null ? null : `${fmtYearsOut(daysRemaining)} out`;
  const elapsed = elapsedTermPercent(lease, today, currentTermStart);

  return (
    <div
      data-testid="dates-hero-next-key-date"
      style={{
        borderRadius: 11,
        padding: "18px 20px",
        color: T.white,
        background: T.heroNavyGrad,
        display: "flex",
        alignItems: "flex-end",
        minHeight: 170,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {imageUrl && (
        <>
          <img
            data-testid="dates-hero-image"
            src={imageUrl}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 11,
              display: "block",
              pointerEvents: "none",
            }}
          />
          <div style={{
            position: "absolute",
            inset: 0,
            background: "rgba(10,24,46,0.58)",
            borderRadius: 11,
            pointerEvents: "none",
          }} />
        </>
      )}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontSize: T.fsXxxs, fontWeight: T.fwBlack, textTransform: "uppercase", letterSpacing: "0.08em", color: T.heroKickerBlue }}>
          Next key date
        </div>
        {item ? (
          <>
            <div style={{ fontSize: T.fsLg7, fontWeight: T.fwExtrabold, marginTop: 3 }}>
              <span data-testid="dates-hero-event-label">{KEY_DATE_LABEL[item.type] || BUCKET_LABEL[item.type] || "Key date"}</span>
              <span> · </span>
              <span data-testid="dates-hero-formatted-date">{fmtDate(item.dueDate)}</span>
            </div>
            <div data-testid="dates-hero-term-metadata" style={{ fontSize: T.fsSm, color: T.heroSubBlue, fontWeight: T.fwSemibold, marginTop: 2 }}>
              {termMetadata(lease, currentTermStart)}
            </div>
            <div
              data-testid="dates-hero-countdown"
              style={{
                alignSelf: "flex-start",
                marginTop: 5,
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 7,
                padding: "4px 10px",
              }}
            >
              <span data-testid="dates-hero-years-label">{countdown}</span>
              {elapsed != null && <span> · {elapsed}% of term elapsed</span>}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: T.fsBase, opacity: 0.85, marginTop: 4 }}>
              No upcoming key dates on file.
            </div>
            <div data-testid="dates-hero-term-metadata" style={{ fontSize: T.fsSm, color: T.heroSubBlue, fontWeight: T.fwSemibold }}>
              {termMetadata(lease, currentTermStart)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DateFlagsPane({ criticalDates, lease, today }) {
  const selection = selectCurrentTermKeyDates(criticalDates, lease, today);
  const noticeWindow = selectCurrentTermNoticeWindow(
    criticalDates,
    lease,
    today,
    new Set(["renewal_notice", "notice_deadline", "break_notice"]),
  );
  const notice = noticeWindow.state === "no-notice"
    ? null
    : { dueDate: noticeWindow.noticeDateValue };
  const breakOption = nearestCurrentTermDate(
    selection.windowDates,
    new Set(["break_option"]),
    today,
  );
  const renewal = nearestCurrentTermDate(
    selection.eligibleDates,
    new Set(["renewal_option"]),
    today,
  );
  const expiry = realLeaseEndDate(lease)
    || nearestCurrentTermDate(selection.eligibleDates, new Set(["expiration"]), today)
    || null;
  const renewalLabel = renewalTermLabel(lease);
  const rows = [
    { label: "Expiry", value: expiry ? fmtDate(expiry.dueDate || expiry.raw) : null, color: T.ragRed },
    { label: "Notice window", value: notice ? fmtDate(notice.dueDate) : "No current-term window", color: T.ragAmber },
    { label: "Break option", value: breakOption ? fmtDate(breakOption.dueDate) : "No current-term window", color: T.ragAmber },
    { label: "Renewal", value: renewal ? (renewalLabel || fmtDate(renewal.dueDate)) : null, color: T.purple },
  ];

  return (
    <div
      data-testid="dates-date-flags"
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 11,
        padding: "15px 18px",
        background: T.card,
        display: "flex",
        flexDirection: "column",
        minHeight: 170,
        boxSizing: "border-box",
      }}
    >
      <div style={{ fontSize: T.fsXxxs, fontWeight: T.fwBlack, letterSpacing: "0.06em", textTransform: "uppercase", color: T.mid, marginBottom: 5 }}>
        Date flags
      </div>
      {rows.map(({ label, value, color }) => (
        <div
          key={label}
          data-testid={`dates-flag-${label.toLowerCase().replace(/\s+/g, "-")}`}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: "5px 2px",
            borderBottom: label === "Renewal" ? "none" : `1px solid ${T.bg}`,
            minHeight: 28,
          }}
        >
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark }}>{label}</span>
          <span
            data-testid={`dates-flag-value-${label.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              marginLeft: "auto",
              fontSize: T.fsXs,
              fontWeight: value ? T.fwBold : T.fwRegular,
              color: value ? T.dark : T.faint,
              textAlign: "right",
            }}
          >
            {value || "None on file"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── DatesLeaseTimeline ───────────────────────────────────────────────────────
// SVG proportional timeline. Events placed between lease start and end.
// Shows commencement, expiry, critical dates, abatement band, and a "Now" marker.

const DOT_COLOR = {
  commencement:      T.green,
  expiry_anchor:     T.urgentRed,
  renewal_notice:    T.purple,
  break_option:      T.ragAmber,
  break_notice:      T.purple,
  expiration:        T.urgentRed,
  escalation:        T.mid,
  escalation_group:  T.mid,
  deposit_burndown:  T.forestGreen,
  allowance_deadline:T.spacebaseBlue,
  allowance_warning: T.mid,
  allowance_expiry:  T.mid,
  cam_audit:         T.spacebaseBlue,
  other:             T.mid,
};

const LEGEND_ITEMS = [
  { type: "commencement",     label: "Start" },
  { type: "expiry_anchor",    label: "Expiry" },
  { type: "escalation_group", label: "Escalation" },
  { type: "renewal_notice",   label: "Renewal option" },
  { type: "break_option",     label: "Break option" },
];

// Truncate a label to at most `max` visible characters for SVG text.
function truncLabel(str, max = 18) {
  if (!str) return "";
  return str.length > max ? str.slice(0, max - 1) + "\u2026" : str;
}

// SVG viewBox width constant — all x coordinates are computed in this space.
const SVG_W = 1120;
const SVG_H = 110;
// Track sits at y=52 in the 110-high viewBox.
const TRACK_Y = 52;

// Label placement constants (exported for unit-testing)
export const LABEL_BELOW_Y      = SVG_H - 18;   // 92  — below the track
export const LABEL_ABOVE_Y      = TRACK_Y - 12; // 40  — above the track
export const LABEL_ABOVE_FAR_Y  = TRACK_Y - 22; // 30  — third level (both sides taken)
export const LABEL_BELOW_FAR_Y  = 104;           // 104 — fourth level, below the track (overflow: visible)
export const LABEL_BELOW_FAR_Y2 = 116;           // 116 — fifth level, SVG overflow zone (handles 5+ clusters)
// Events whose x-coordinates are within this many SVG units (~12% of width) get staggered.
// An 18-char truncated label spans ~99 SVG units at fontSize=9, so we need at least
// 2 × 50 (half-label) + 30 breathing room = 130 to avoid visual edge overlap.
export const LABEL_COLLISION_PX = 130;

/**
 * Assign non-overlapping label y-positions to a set of labeled timeline events.
 *
 * Events are processed left-to-right. For each event, all already-placed events
 * within LABEL_COLLISION_PX SVG units are considered "occupied". The first
 * available level from the preference list is assigned; if all five levels are
 * taken, the last (furthest) level is reused as a safety net.
 *
 * The SVG element uses overflow:visible so levels 104 and 116 render correctly
 * even though SVG_H = 110.
 *
 * @param {Array<{key: string, x: number, preferAbove: boolean}>} labeledEvents
 * @param {number} [nowX]  Optional SVG x-coordinate of the "Now" marker.
 *   When supplied, a phantom entry at nowX is injected that pre-occupies
 *   LABEL_ABOVE_Y and LABEL_ABOVE_FAR_Y, preventing real events within
 *   LABEL_COLLISION_PX of the Now marker from being placed in those levels.
 * @returns {Map<string, number>}  key → SVG y for the label text element
 */
export function computeLabelYs(labeledEvents, nowX) {
  const result = new Map();

  // When nowX is supplied, inject two phantom entries that pre-occupy the
  // "above" levels at the Now marker position so real events near it are
  // pushed to a "below" level instead.
  const phantoms = [];
  if (nowX != null) {
    phantoms.push({ key: "__now_above",     x: nowX, _phantom: true });
    phantoms.push({ key: "__now_above_far", x: nowX, _phantom: true });
    result.set("__now_above",     LABEL_ABOVE_Y);
    result.set("__now_above_far", LABEL_ABOVE_FAR_Y);
  }

  const sorted = [...labeledEvents, ...phantoms].sort((a, b) => a.x - b.x);

  for (const ev of sorted) {
    // Phantoms are pre-set; skip them in the assignment loop.
    if (ev._phantom) continue;

    // Collect y-levels already used by placed events within the collision radius.
    const occupiedY = new Set(
      sorted
        .filter((other) => other !== ev && result.has(other.key) && Math.abs(ev.x - other.x) < LABEL_COLLISION_PX)
        .map((other) => result.get(other.key)),
    );

    // Try levels in preference order; pick the first free one.
    const levels = ev.preferAbove
      ? [LABEL_ABOVE_Y, LABEL_BELOW_Y, LABEL_ABOVE_FAR_Y, LABEL_BELOW_FAR_Y, LABEL_BELOW_FAR_Y2]
      : [LABEL_BELOW_Y, LABEL_ABOVE_Y, LABEL_ABOVE_FAR_Y, LABEL_BELOW_FAR_Y, LABEL_BELOW_FAR_Y2];

    const assignedY = levels.find((y) => !occupiedY.has(y)) ?? levels[levels.length - 1];
    result.set(ev.key, assignedY);
  }

  // Remove phantom keys so callers only see real-event entries.
  result.delete("__now_above");
  result.delete("__now_above_far");

  return result;
}

/**
 * Clamp a label's SVG x coordinate so its text stays within the viewport.
 * Returns { x, anchor } — anchor switches to "start" near the left edge and
 * "end" near the right edge so the text expands inward instead of clipping.
 *
 * @param {number} rawX     — unclamped SVG x coordinate of the dot
 * @param {number} halfWidth — estimated half-width of the rendered text in SVG units
 */
function clampLabelX(rawX, halfWidth = 55) {
  if (rawX < halfWidth) {
    return { x: halfWidth, anchor: "start" };
  }
  if (rawX > SVG_W - halfWidth) {
    return { x: SVG_W - halfWidth, anchor: "end" };
  }
  return { x: rawX, anchor: "middle" };
}

export function DatesLeaseTimeline({ events, abatementBands, lease, today }) {
  const intent = useChartHoverIntent(JSON.stringify(events));
  const hoveredDot = intent.open ? intent.point : null;

  const allDates = events.map((e) => parseDateOnly(e.date)).filter(Boolean);
  if (allDates.length < 1) return null;

  const now = today || new Date();

  const minDate = allDates.reduce((a, b) => (a < b ? a : b));
  const maxDate = allDates.reduce((a, b) => (a > b ? a : b));

  const rangeStart = now < minDate ? now : minDate;
  const rangeEnd   = now > maxDate ? now : maxDate;

  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  if (totalMs <= 0) return null;

  // Convert a date to an SVG x coordinate (0–SVG_W).
  function xPx(d) {
    const dt = parseDateOnly(d);
    if (!dt) return null;
    const p = (dt.getTime() - rangeStart.getTime()) / totalMs;
    return Math.min(SVG_W, Math.max(0, p * SVG_W));
  }

  const nowX = Math.min(SVG_W * 0.98, Math.max(SVG_W * 0.02,
    ((now.getTime() - rangeStart.getTime()) / totalMs) * SVG_W,
  ));
  const nowLabel = now.toLocaleDateString("en-US", { month: "short", year: "numeric" });

  // Abatement bands
  const bands = (abatementBands || []).map((b) => ({
    ...b,
    x1: xPx(b.start),
    x2: xPx(b.end),
  })).filter((b) => b.x1 != null && b.x2 != null);

  // Positioned events
  const positioned = events.map((e) => ({
    ...e,
    x: xPx(e.date),
  })).filter((e) => e.x != null);

  // Separate event types for SVG placement
  const commence   = positioned.find((e) => e.type === "commencement");
  const expiryEv   = positioned.find((e) => e.type === "expiry_anchor");
  const renewalEv  = positioned.find((e) => e.type === "renewal_notice");
  const escalGroup = positioned.find((e) => e.type === "escalation_group");
  const others     = positioned.filter(
    (e) => !["commencement", "expiry_anchor", "renewal_notice", "escalation_group"].includes(e.type),
  );

  // Helper: show tooltip for a dot event.
  // Builds a compact one-line tooltipText: label + first clause of description (~50 chars).
  function dotHandlers(e) {
    const firstClause = e.description
      ? e.description.split(/[.,]/)[0].trim().slice(0, 50)
      : null;
    const tooltipText = firstClause ? `${e.label} · ${firstClause}` : e.label;
    return {
      ...intent.pointProps({ tooltipText, date: fmtDate(e.date), xPct: e.x / SVG_W }),
      "aria-label": `${tooltipText} · ${fmtDate(e.date)}`,
    };
  }

  return (
    <div
      data-testid="dates-timeline"
      {...intent.containerProps}
      style={{
        border:       `1px solid ${T.border}`,
        borderRadius: 10,
        padding:      "16px 20px 12px",
        background:   T.card,
        position:     "relative",
      }}
    >
      <GroupHeader label="Lease timeline" />

      {/* Hover tooltip overlay — positioned above the hovered dot */}
      {hoveredDot && (
        <div
          data-testid="dates-timeline-tooltip"
          style={{
            position:      "absolute",
            bottom:        "calc(100% - 28px)",
            left:          `calc(20px + ${(hoveredDot.xPct * 100).toFixed(1)}% - ${(hoveredDot.xPct * 40).toFixed(1)}px)`,
            transform:     "translateX(-50%)",
            background:    T.tooltip?.bg ?? T.dark,
            color:         T.tooltipText ?? T.white,
            borderRadius:  6,
            padding:       "5px 10px",
            fontSize:      T.fsXxs,
            fontWeight:    T.fwSemibold,
            pointerEvents: "none",
            zIndex:        10,
            whiteSpace:    "nowrap",
            boxShadow:     "0 2px 8px rgba(0,0,0,0.18)",
          }}
        >
          <span style={{
            display:       "inline-block",
            maxWidth:      320,
            overflow:      "hidden",
            textOverflow:  "ellipsis",
            whiteSpace:    "nowrap",
            verticalAlign: "middle",
          }}>
            {hoveredDot.tooltipText}
          </span>
          {hoveredDot.date && (
            <span style={{ fontWeight: T.fwNormal ?? 400, opacity: 0.85, marginLeft: 6, verticalAlign: "middle" }}>
              · {hoveredDot.date}
            </span>
          )}
        </div>
      )}

      {/* SVG track */}
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Base track line */}
        <line x1={0} y1={TRACK_Y} x2={SVG_W} y2={TRACK_Y} stroke={T.border} strokeWidth={3} strokeLinecap="round" />

        {/* Elapsed segment — Start → Today */}
        {commence && nowX > (commence?.x ?? 0) && (
          <line
            x1={commence.x} y1={TRACK_Y}
            x2={nowX} y2={TRACK_Y}
            stroke={T.timelineBlue} strokeWidth={3} strokeLinecap="round"
          />
        )}

        {/* Abatement bands */}
        {bands.map((b, i) => (
          <g key={i}>
            <rect
              x={b.x1} y={TRACK_Y - 5}
              width={Math.max(0, b.x2 - b.x1)} height={10}
              fill={T.green} opacity={0.3} rx={2}
            />
            <text
              x={(b.x1 + b.x2) / 2} y={TRACK_Y - 10}
              textAnchor="middle"
              fontSize={9} fill={T.ragGreenText} fontWeight={600}
            >
              {b.label}
            </text>
          </g>
        ))}

        {/* Start dot — solid filled, hover tooltip */}
        {commence && (
          <g
            data-testid="dates-timeline-commence"
            style={{ cursor: "help" }}
            {...dotHandlers(commence)}
          >
            <circle cx={commence.x} cy={TRACK_Y} r={6} fill={T.green} />
          </g>
        )}

        {/* Expiry dot — solid filled red (matches legend swatch), hover tooltip */}
        {expiryEv && (
          <g
            data-testid="dates-timeline-expiry"
            style={{ cursor: "help" }}
            {...dotHandlers(expiryEv)}
          >
            <circle cx={expiryEv.x} cy={TRACK_Y} r={6} fill={T.urgentRed} />
          </g>
        )}

        {/* Renewal-notice dot — solid filled purple, hover tooltip */}
        {renewalEv && (
          <g
            data-testid="dates-timeline-renewal"
            style={{ cursor: "help" }}
            {...dotHandlers(renewalEv)}
          >
            <circle cx={renewalEv.x} cy={TRACK_Y} r={6} fill={T.purple} />
          </g>
        )}

        {/* Escalation group — badge above dot, hover tooltip */}
        {escalGroup && (
          <g
            data-testid="dates-timeline-escalation"
            style={{ cursor: "help" }}
            {...dotHandlers(escalGroup)}
          >
            <circle cx={escalGroup.x} cy={TRACK_Y} r={5} fill={T.mid} />
            {escalGroup.count > 1 && (() => {
              // Nudge the badge left when it would sit directly behind the Now marker.
              const badgeOffset = Math.abs(escalGroup.x - nowX) < 50 ? -28 : 0;
              const rawBx = escalGroup.x + badgeOffset;
              // Clamp badge so rect never starts at a negative x or clips right edge.
              const bx = Math.max(13, Math.min(SVG_W - 13, rawBx));
              return (
                <g data-testid="dates-timeline-escalation-badge">
                  <rect
                    x={bx - 11} y={TRACK_Y - 26}
                    width={22} height={13}
                    fill={T.lightGray} rx={3}
                  />
                  <text
                    x={bx} y={TRACK_Y - 16}
                    textAnchor="middle"
                    fontSize={9} fill={T.mid} fontWeight={700}
                  >
                    ×{escalGroup.count}
                  </text>
                </g>
              );
            })()}
          </g>
        )}

        {/* Other event dots — solid dot, hover tooltip */}
        {others.map((e, i) => {
          const color = DOT_COLOR[e.type] || T.mid;
          return (
            <g
              key={e.id != null ? `other-${e.id}` : `other-i-${i}`}
              data-testid={e.id != null ? `dates-timeline-other-${e.id}` : `dates-timeline-other-i-${i}`}
              style={{ cursor: "help" }}
              {...dotHandlers(e)}
            >
              <circle cx={e.x} cy={TRACK_Y} r={5} fill={color} />
            </g>
          );
        })}

        {/* Now marker — vertical line + label above */}
        {(() => {
          const nowCl = clampLabelX(nowX, 48);
          return (
            <g data-testid="dates-timeline-now">
              <line
                x1={nowX} y1={30}
                x2={nowX} y2={TRACK_Y + 8}
                stroke={T.dark} strokeWidth={1.5} strokeDasharray="3 2" opacity={0.6}
              />
              <text
                x={nowCl.x} y={22}
                textAnchor={nowCl.anchor}
                fontSize={9} fill={T.dark} fontWeight={700}
              >
                Now · {nowLabel}
              </text>
            </g>
          );
        })()}
      </svg>

      {/* Range labels */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>
        <span>{rangeStart.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
        <span>{rangeEnd.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 8 }}>
        {LEGEND_ITEMS.map((item) => (
          <div key={item.type} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{
              width:        7,
              height:       7,
              borderRadius: "50%",
              background:   DOT_COLOR[item.type] || T.mid,
              flexShrink:   0,
            }} />
            <span style={{ fontSize: T.fsXxxs, color: T.mid }}>{item.label}</span>
          </div>
        ))}
        {bands.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 12, height: 7, borderRadius: 2, background: T.ragGreenBg, border: `1px solid ${T.ragGreenBorder}`, flexShrink: 0 }} />
            <span style={{ fontSize: T.fsXxxs, color: T.mid }}>Abatement</span>
          </div>
        )}
        <span style={{ fontSize: T.fsXxxs, color: T.faint, fontStyle: "italic", marginLeft: 4 }}>
          Hover any dot for details
        </span>
      </div>
    </div>
  );
}

// ─── HistoryList ──────────────────────────────────────────────────────────────
// Past dates, most recent first. No "overdue" label or red styling.
// Clause text + citations behind "details ▾" expander.
// Lists > 5 rows collapse with "Show all N ▾".
// needsReview=true + action-bucket type → subtle amber "needs attention" pill.
// (All other past events remain neutral — informational history has no urgency.)

const HISTORY_COLLAPSE_THRESHOLD = 5;

// ── History row title helpers ─────────────────────────────────────────────────

const HISTORY_TITLE_MAX = 60;

/**
 * Derive a concise one-line title for a history row.
 *
 * - Non-"other" types → short bucket label (e.g. "Escalation").
 * - "other" with a short cd.label (≤ HISTORY_TITLE_MAX) → use the label as-is.
 * - "other" otherwise → take the first sentence of cd.description (split on
 *   `. ` or `.\n`) and truncate to HISTORY_TITLE_MAX chars at the last word
 *   boundary, appending `…`.
 */
function deriveHistoryTitle(cd) {
  if (cd.type !== "other") {
    return BUCKET_LABEL[cd.type] || "Other";
  }
  // Prefer a short, explicit label.
  if (cd.label && cd.label.length <= HISTORY_TITLE_MAX) {
    return cd.label;
  }
  const source = cd.description || cd.label || "";
  if (!source) return BUCKET_LABEL[cd.type] || "Other";
  // Take the first sentence.
  const sentenceMatch = source.match(/^(.*?)\.\s/s);
  const firstSentence = sentenceMatch ? sentenceMatch[1] : source;
  if (firstSentence.length <= HISTORY_TITLE_MAX) {
    return firstSentence;
  }
  // Truncate at the last word boundary within the limit.
  const cut = firstSentence.slice(0, HISTORY_TITLE_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + "…";
}

function HistoryRow({ cd }) {
  const [expanded, setExpanded] = useState(false);
  const hasBody = !!(cd.description || cd.citations);
  const formatted = fmtDate(cd.dueDate);
  const shortLabel = deriveHistoryTitle(cd);

  // needsReview marker applies only to action-bucket types;
  // informational past events (escalation, etc.) remain fully neutral.
  const showNeedsAttention = cd.needsReview && ACTION_BUCKETS.has(cd.type);

  return (
    <div
      data-testid="dates-history-row"
      style={{
        padding:      "8px 0",
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      {/* ── Tabular single-line row ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <TypeTag type={cdTagType(cd.type)} />
        <span style={{ width: 120, flexShrink: 0, fontSize: T.fsSm, color: T.mid }}>{formatted}</span>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold }}>{shortLabel}</span>
          {showNeedsAttention && (
            <span
              data-testid="dates-history-needs-attention"
              style={{
                fontSize:     T.fsXxs,
                fontWeight:   T.fwSemibold,
                background:   T.ragAmberBg,
                color:        T.ragAmberText,
                border:       `1px solid ${T.ragAmberBorder}`,
                borderRadius: 5,
                padding:      "1px 7px",
              }}
            >
              needs attention
            </span>
          )}
        </div>
        {hasBody && (
          <button
            type="button"
            data-testid="dates-history-expander"
            onClick={() => setExpanded((v) => !v)}
            style={{
              flexShrink:  0,
              background:  "none",
              border:      "none",
              padding:     "0 2px",
              cursor:      "pointer",
              color:       T.mid,
              fontSize:    T.fsXxs,
              fontWeight:  T.fwSemibold,
              whiteSpace:  "nowrap",
            }}
          >
            details {expanded ? "▴" : "▾"}
          </button>
        )}
      </div>
      {expanded && hasBody && (
        <div
          style={{
            margin:       "6px 0 4px 100px",
            padding:      "9px 12px",
            background:   T.tableHeadBg,
            border:       `1px solid ${T.bg}`,
            borderRadius: 9,
          }}
        >
          {cd.description && (
            <div style={{ fontSize: T.fsXs, color: T.detailPanelText, whiteSpace: "pre-wrap", lineHeight: 1.55, marginBottom: cd.citations ? 4 : 0 }}>
              {cd.description}
            </div>
          )}
          {cd.citations && (
            <div style={{ fontSize: T.fsXxs, color: T.detailPanelCite, fontStyle: "italic" }}>
              {cd.citations}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryList({ items }) {
  const [showAll, setShowAll] = useState(false);
  if (items.length === 0) return null;

  const collapsed = !showAll && items.length > HISTORY_COLLAPSE_THRESHOLD;
  const visible   = collapsed ? items.slice(0, HISTORY_COLLAPSE_THRESHOLD) : items;

  return (
    <div data-testid="dates-history-list" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "16px 20px", boxSizing: "border-box" }}>
      <GroupHeader label="History" count={items.length} />
      <div>
        {visible.map((cd) => (
          <HistoryRow key={cd.id} cd={cd} />
        ))}
      </div>
      {items.length > HISTORY_COLLAPSE_THRESHOLD && (
        <button
          type="button"
          data-testid="dates-history-show-all"
          onClick={() => setShowAll((v) => !v)}
          style={{
            marginTop:    8,
            background:   "none",
            border:       "none",
            padding:      "4px 0",
            cursor:       "pointer",
            fontSize:     T.fsXs,
            fontWeight:   T.fwSemibold,
            color:        T.blue,
          }}
        >
          {showAll ? "Show fewer ▴" : `Show all ${items.length} ▾`}
        </button>
      )}
    </div>
  );
}

// ─── LeaseDatesPane ───────────────────────────────────────────────────────────

/**
 * @param {Array}    criticalDates        — raw rows from listCriticalDates()
 * @param {boolean}  canWrite             — true for editor/admin
 * @param {string}   locationId           — passed to addCriticalDate
 * @param {string}   leaseId              — passed to addCriticalDate
 * @param {object}   lease                — selectedLease (for timeline anchors)
 * @param {Array}    abatementAllowances  — allowances filtered to type === 'abatement'
 * @param {Function} onDateAdded          — optional callback after create
 */
export function LeaseDatesPane({
  criticalDates = [],
  locationId,
  leaseId,
  lease = null,
  abatementAllowances = [],
  onDateAdded,
}) {
  const groups = useMemo(() => groupCriticalDates(criticalDates, new Date(), lease), [criticalDates, lease]);

  const { events: timelineEvents, abatementBands } = useMemo(
    () => buildTimelineEvents(criticalDates, lease, abatementAllowances),
    [criticalDates, lease, abatementAllowances],
  );

  const today = useMemo(() => new Date(), []);
  const todayMidnight = useMemo(() => {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [today]);
  const nextKeyDate = useMemo(
    () => getNextKeyDate(criticalDates, lease, todayMidnight),
    [criticalDates, lease, todayMidnight],
  );
  const currentTermStart = useMemo(
    () => selectCurrentTermKeyDates(criticalDates, lease, todayMidnight).currentTermStart,
    [criticalDates, lease, todayMidnight],
  );

  const [showAddForm,       setShowAddForm]       = useState(false);
  const [newType,           setNewType]           = useState("renewal_notice");
  const [newDueDate,        setNewDueDate]        = useState("");
  const [formSaving,        setFormSaving]        = useState(false);
  const [formError,         setFormError]         = useState(null);
  const [heroImageUrl,      setHeroImageUrl]      = useState(null);

  useEffect(() => {
    getPulseCategoryImages().then((result) => {
      setHeroImageUrl(result["Decision"] ?? null);
    });
  }, []);

  async function handleFormSubmit() {
    if (!newDueDate || formSaving) return;
    setFormSaving(true);
    setFormError(null);
    try {
      await addCriticalDate(locationId, leaseId, { type: newType, dueDate: newDueDate });
      setShowAddForm(false);
      setNewType("renewal_notice");
      setNewDueDate("");
      if (onDateAdded) onDateAdded();
    } catch (err) {
      setFormError(err.message || "Failed to save - please try again.");
    } finally {
      setFormSaving(false);
    }
  }

  return (
    <div data-testid="lease-dates-pane" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Row 1: Next key date + Date flags ─────────────────────────────── */}
      <div style={{
        display:             "grid",
        gridTemplateColumns: "1.5fr 1fr",
        gap:                 12,
        alignItems:          "stretch",
      }}>
        <NextKeyDateHero
          item={nextKeyDate}
          lease={lease}
          today={todayMidnight}
          currentTermStart={currentTermStart}
          imageUrl={heroImageUrl}
        />
        <DateFlagsPane criticalDates={criticalDates} lease={lease} today={todayMidnight} />
      </div>

      {/* ── Inline add-date form ─────────────────────────────────────────── */}
      {showAddForm && (
        <div
          data-testid="dates-add-form"
          style={{
            background:   T.card,
            border:       `1px solid ${T.border}`,
            borderRadius: 10,
            padding:      "14px 16px",
          }}
        >
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, marginBottom: 10 }}>
            Add critical date
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: T.fsXs, color: T.mid, display: "block", marginBottom: 4 }}>Type</label>
              <select
                data-testid="dates-add-type"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                style={{ width: "100%", fontSize: T.fsSm, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.border}` }}
              >
                {CD_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: T.fsXs, color: T.mid, display: "block", marginBottom: 4 }}>Due date</label>
              <input
                type="date"
                data-testid="dates-add-due"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                style={{ width: "100%", fontSize: T.fsSm, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}` }}
              />
            </div>
            <button
              type="button"
              data-testid="dates-add-submit"
              disabled={formSaving || !newDueDate}
              onClick={handleFormSubmit}
              style={{
                fontSize:     T.fsSm,
                padding:      "6px 14px",
                borderRadius: 6,
                background:   T.blue,
                color:        T.white,
                border:       "none",
                cursor:       "pointer",
                fontWeight:   T.fwSemibold,
              }}
            >
              {formSaving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setFormError(null); }}
              style={{
                fontSize:     T.fsSm,
                padding:      "6px 10px",
                borderRadius: 6,
                background:   T.lightGray,
                color:        T.mid,
                border:       "none",
                cursor:       "pointer",
              }}
            >
              Cancel
            </button>
          </div>
          {formError && (
            <div style={{ fontSize: T.fsXs, color: T.red, marginTop: 6 }}>{formError}</div>
          )}
        </div>
      )}

      {/* ── SVG Timeline ─────────────────────────────────────────────────── */}
      <DatesLeaseTimeline
        events={timelineEvents}
        abatementBands={abatementBands}
        lease={lease}
        today={today}
      />

      {/* ── History: full-width bottom row ────────────────────────────────── */}
      <HistoryList items={groups.history} />

    </div>
  );
}
